import { useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';

// Tecton
import { useGetFcos } from '../../../api/fcos';
import { WorkspaceFCOContainer } from '../../../core/types/fcoTypes';
import { useUserSettings } from '../../context/UserSettingsContext';
import TransformationOverview from './TransformationOverview';
import { TransformationRoutes, getTransformationRouteName, getTransformationRoutes } from './transformationsUtils';

// @tecton
import { Loading, ViewLayout } from '@lithos';

import FeatureViewsList from '../featureViews/FeatureViewsList';

const TransformationContainerOutlet = () => {
  /*
    This contains the header portion of the Feature view
    - Breadcrumbs
    - Tabs
    - Outlet to the content
  */
  const navigation = useNavigate();
  const { name } = useParams();
  const { workspace } = useUserSettings();
  const location = useLocation();
  const featureRoutes = getTransformationRouteName(location.pathname);
  const [selectedTab, setSelectedTab] = useState<TransformationRoutes>(featureRoutes);

  const routeNames = Object.keys(getTransformationRoutes());

  const headerProps = {
    breadcrumbs: {
      root: { label: workspace ?? '', url: `/repo/${workspace}` },
      items: [{ label: 'Transformations', url: `/repo/${workspace}/transformations` }, { label: name }],
    },
    title: name,
    tabs: {
      items: routeNames.map((routeName) => ({ label: routeName, value: routeName })),
      selectedTab: selectedTab,
      setSelectedTab: (tab: TransformationRoutes) => {
        const routes = getTransformationRoutes();
        navigation(routes[tab]);
        setSelectedTab(tab);
      },
    },
  };

  return <ViewLayout header={headerProps} content={<Outlet />} />;
};

const TransformationContainer = () => {
  const { workspace } = useUserSettings();
  const { name } = useParams();
  const { data, isLoading } = useGetFcos(workspace ?? '', {
    select: (data: WorkspaceFCOContainer) => {
      return {
        transformation: data?.transformations.find((transformation) => {
          return transformation?.name === name;
        }),
        featureViews: data?.featureViews,
        idToFcoMap: data?.idToFcoMap,
      };
    },
  });

  const dependentFeatureViews =
    data?.transformation?.dependentFeatureViews?.map((id: string) => data.idToFcoMap[id]) ?? [];

  if (isLoading) {
    return <></>;
  }

  if (!data?.transformation) {
    // We switched workspaces and didn't find the transformation name, so we redirect them to the transformation lists
    return <Navigate to={`../${workspace}/transformations`} />;
  }

  const transformationRoutes = getTransformationRoutes();

  return (
    <Routes>
      <Route element={<TransformationContainerOutlet />}>
        <Route index element={<Navigate to={transformationRoutes[`Metadata`]} replace />} />
        <Route
          path={transformationRoutes[`Metadata`]}
          element={<TransformationOverview transformation={data?.transformation} />}
        />
        <Route
          path={transformationRoutes[`Feature Views`]}
          element={
            <FeatureViewsList items={dependentFeatureViews} idToFcoMap={data.idToFcoMap} workspace={workspace ?? ''} />
          }
        />
      </Route>
    </Routes>
  );
};

export default TransformationContainer;
